import { permits } from './navigation'
import { tabFor } from './tabs'

/**
 * Every screen this console has, and every tab inside the four screens that have any.
 *
 * `navigation.ts` is the list of places an operator can *go*; this is the list of screens that
 * exist. The two are deliberately different sizes. Five destinations carry fourteen screens between
 * them, because seven screens became tabs of another and six more are reached by opening a row, and
 * none of those appear in the navigation at all.
 *
 * It exists because four screens each held their own tab list and nothing could see all four. That
 * was fine while the only thing needing to know was the screen itself, and stopped being fine the
 * moment something outside a screen had to answer a question about it - which is what per-screen help
 * is. The alternative was a second hand-written copy of four lists, which `navigation.ts`'s own
 * comment names as how two renderers stop agreeing about what exists.
 *
 * So this is rendered *from*, not merely read: the four tab hosts take their keys, labels, default and
 * role gates from here. That is the property everything downstream rests on - **a tab that renders is
 * a tab that is declared** - and it is why `help.test.ts` asserts no host has quietly grown a list of
 * its own again. A completeness gate over a table that is also the only inventory is a comparison of
 * one list against itself.
 *
 * Every `route` is spelled exactly as `App.tsx` declares it, `:param` segments included, because
 * `help.test.ts` reads that file and compares the two sets. A route added to the router and not to
 * this list fails a unit test rather than quietly having no help.
 */
export interface ScreenTab {
  /**
   * The `?tab=` value, which is not the label and must never be tidied into it.
   *
   * `deployment` is the tab labelled Enrollment. `/deployment` redirects onto that key, the
   * end-to-end suite drives the spelling, and the redirects are kept indefinitely because bookmarks
   * exist - so renaming the key to match the word on the button would break every link anybody has
   * pasted into a ticket in order to tidy a query string nobody reads (D-429).
   */
  key: string
  label: string
  /**
   * The roles that may open this tab. Absent means everybody signed in.
   *
   * The same meaning, and the same non-meaning, as `NavItem.roles`: it hides what somebody cannot
   * use and protects nothing. The API refuses the request either way.
   */
  roles?: readonly string[]
}

export interface ScreenAlias {
  /** The query parameter, when it is not `tab`. */
  parameter: string
  value: string
  /** The tab this spelling selects. */
  tab: string
}

export interface Screen {
  /** Exactly as `App.tsx` spells it. `/policies/:policyId`, not `/policies/:id`. */
  route: string
  /**
   * What this screen is called in a sentence, and what its tab strip is labelled.
   *
   * One field rather than two, because the four hosts already pass their own name to `aria-label` on
   * the strip, and a screen whose strip is labelled something other than the screen is a screen with
   * two names.
   */
  title: string
  tabs?: readonly ScreenTab[]
  /** The tab a bare path shows. Every host writes the default as a bare path (D-441). */
  defaultTab?: string
  /**
   * Other spellings of a tab that this console already answers.
   *
   * `/events` redirects to `/cases?feed=raw`, so `feed=raw` is a spelling of `tab=raw` rather than a
   * second thing to keep in step. Declaring it as data is what lets the redirect table and the tab be
   * one fact: `CasesPage` used to read `feed` with a `URLSearchParams` of its own and was the one
   * place in the console that answered "which tab" without the helper.
   */
  aliases?: readonly ScreenAlias[]
}

export const SCREENS = [
  { route: '/board', title: 'Board' },
  {
    route: '/cases',
    title: 'Cases',
    tabs: [
      { key: 'cases', label: 'Cases' },
      { key: 'raw', label: 'Raw detections' },
      { key: 'query', label: 'Query' }
    ],
    defaultTab: 'cases',
    aliases: [{ parameter: 'feed', value: 'raw', tab: 'raw' }]
  },
  { route: '/cases/:alertId', title: 'A case' },
  { route: '/people', title: 'People' },
  {
    route: '/policies',
    title: 'Policies',
    tabs: [
      { key: 'policies', label: 'Policies' },
      { key: 'dictionaries', label: 'Dictionaries' },
      { key: 'messages', label: 'Messages' },
      { key: 'assistant', label: 'Assistant' }
    ],
    defaultTab: 'policies'
  },
  { route: '/policies/build', title: 'Build a policy' },
  { route: '/policies/groups/:groupId', title: 'A policy group' },
  { route: '/policies/new', title: 'A new policy' },
  { route: '/policies/:policyId', title: 'The policy editor' },
  { route: '/dictionaries/:dictionaryId', title: 'A dictionary' },
  {
    route: '/devices',
    title: 'Devices',
    tabs: [
      { key: 'devices', label: 'Fleet' },
      { key: 'deployment', label: 'Enrollment' },
      { key: 'checkin', label: 'Check-in' },
      { key: 'trust', label: 'Trust' }
    ],
    defaultTab: 'devices'
  },
  { route: '/devices/:deviceId', title: 'A device' },
  { route: '/events/:eventRecordId', title: 'A detection' },
  { route: '/system', title: 'System' },
  { route: '/export', title: 'Export' },
  {
    route: '/settings',
    title: 'Settings',
    tabs: [
      { key: 'appearance', label: 'Appearance' },
      { key: 'password', label: 'Your password' },
      { key: 'dashboard', label: 'Dashboard' },
      // Owner-only, and filtered out of the strip rather than left to the card's own guard.
      // `ProviderCredentialCard` renders nothing for a non-owner, which was the right answer while it
      // was one card among four and is an empty panel now that it is a tab of its own (D-493).
      { key: 'provider', label: 'Provider', roles: ['owner'] }
    ],
    defaultTab: 'appearance'
  },
  { route: '/accounts', title: 'Accounts' },
  { route: '/branding', title: 'Branding' },
  { route: '/platform/tenants', title: 'Organizations' },
  { route: '/platform/administrators', title: 'Administrators' }
] as const satisfies readonly Screen[]

/**
 * The same list, widened, for everything that iterates it.
 *
 * `SCREENS` is `as const` because `help.ts` derives its key union from these literals, which is what
 * makes a missing help entry a compile error rather than a test failure. The cost of that is a union
 * of nineteen distinct object types, half of which have no `tabs` property at all - so a plain `for`
 * over it cannot reach `screen.tabs` without narrowing at every step. Two names for one array, with
 * the widened one used everywhere the literal types are not the point.
 */
export const ALL_SCREENS: readonly Screen[] = SCREENS

/**
 * One screen by its route, for a host that knows which one it is.
 *
 * Throws rather than returning undefined. Every caller is a component naming its own route as a
 * literal, so an absent entry is a typo at the keyboard rather than a condition to handle at runtime,
 * and a host that silently rendered no tabs would be a screen that had lost its navigation.
 */
export function screenFor(route: string): Screen {
  const found = ALL_SCREENS.find((screen) => screen.route === route)
  if (!found) throw new Error(`No screen is declared for '${route}'.`)
  return found
}

/** The tabs one operator has on this screen, filtered the way the host filters them. */
export function tabsFor(screen: Screen, roles: readonly string[]): readonly ScreenTab[] {
  return (screen.tabs ?? []).filter((tab) => permits(tab.roles, roles))
}

/**
 * The tab a URL selects, resolved against the tabs this operator actually has.
 *
 * Wraps `tabFor` rather than reimplementing it, and resolves against the *filtered* set, which is the
 * order `SettingsPage` established and the reason it matters: a pasted `?tab=provider` an analyst
 * cannot open has to fall back to Appearance rather than select a panel with nothing in it. Filtering
 * afterwards would select the tab and then render nothing.
 *
 * A declared alias is honored first, matching the order `CasesPage` read its two parameters in, so
 * `?feed=raw&tab=query` is the raw feed. Null for a screen that has no tabs.
 */
export function activeTabFor(screen: Screen, search: string, roles: readonly string[]): string | null {
  const allowed = tabsFor(screen, roles)
  if (allowed.length === 0) return null

  const keys = allowed.map((tab) => tab.key)
  const parameters = new URLSearchParams(search)

  for (const alias of screen.aliases ?? []) {
    if (parameters.get(alias.parameter) === alias.value && keys.includes(alias.tab)) return alias.tab
  }

  return tabFor(search, keys, screen.defaultTab ?? keys[0]!)
}
